/**
 * MentionPromptField Component
 *
 * Reusable component for AI prompt fields with "@" mention functionality.
 * Provides Expression/Result toggle for preview modes and data hydration.
 *
 * Features:
 * - "@" mention support via RichMentionEditorWrapper
 * - Expression mode: human-readable preview with names
 * - Result mode: hydrated JSON values for AI submission
 * - Theme-integrated styling
 * - Error/validation states
 * - Accessibility support
 *
 * Usage:
 * ```tsx
 * <MentionPromptField
 *   label="AI Prompt"
 *   value={prompt}
 *   onChange={setPrompt}
 *   projectId={projectId}
 *   currentUser={currentUser}
 *   dataContext={dataContext}
 *   onHydratedResultChange={setHydratedPrompt}
 *   placeholder="Describe using @ to reference data..."
 * />
 * ```
 */

import React, { useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  FormHelperText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { RichMentionEditorWrapper } from '@modules/ui-components/wrappers/RichMentionEditorWrapper';
import { useMentionPrompt } from './useMentionPrompt';
import type { MentionPromptFieldProps } from './types';

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * MentionPromptField Component
 */
const MentionPromptField: React.FC<MentionPromptFieldProps> = ({
  // Core props
  value,
  onChange,
  projectId,
  currentUser,
  project,
  dataContext,

  // Preview mode control
  previewMode: controlledPreviewMode,
  onPreviewModeChange,

  // Hydration output
  onHydratedResultChange,

  // UI configuration
  label,
  placeholder,
  disabled = false,
  readOnly = false,

  // Validation
  error = false,
  errorMessage,
  helperText,

  // Data references (future)
  showDataReferences,
  selectedDataPaths,
  onDataPathsChange,

  // Styling
  sx,
  minHeight,
  maxHeight,

  // Testing
  hydrationService,
}) => {
  const theme = useTheme();

  // ===========================================================================
  // HOOK
  // ===========================================================================

  const {
    value: internalValue,
    previewMode: internalPreviewMode,
    previewContent,
    isProcessing,
    setValue: setInternalValue,
    setPreviewMode: setInternalPreviewMode,
    hydratedResult,
    editorProps,
  } = useMentionPrompt({
    initialValue: value,
    dataContext,
    onHydratedResultChange,
    hydrationService,
  });

  // Controlled vs uncontrolled preview mode
  const currentPreviewMode = controlledPreviewMode ?? internalPreviewMode;

  // ===========================================================================
  // SYNC WITH PARENT
  // ===========================================================================

  // Sync value from parent
  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===========================================================================
  // HANDLERS
  // ===========================================================================

  /**
   * Handle editor content change
   */
  const handleChange = useCallback(
    (newValue: string) => {
      setInternalValue(newValue);
      onChange(newValue);
    },
    [setInternalValue, onChange]
  );

  /**
   * Handle preview mode toggle
   */
  const handlePreviewModeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newMode: 'expression' | 'result' | null) => {
      if (newMode === null) return; // Prevent deselecting

      setInternalPreviewMode(newMode);
      onPreviewModeChange?.(newMode);
    },
    [setInternalPreviewMode, onPreviewModeChange]
  );

  // ===========================================================================
  // STYLES
  // ===========================================================================

  const containerStyles: SxProps<Theme> = {
    width: '100%',
    ...(disabled && {
      opacity: 0.6,
      pointerEvents: 'none',
    }),
    ...sx,
  };

  const labelStyles: SxProps<Theme> = {
    mb: 1,
    fontWeight: 500,
    color: error ? 'error.main' : 'text.primary',
  };

  const toggleGroupStyles: SxProps<Theme> = {
    mb: 1,
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      px: 2,
      py: 0.5,
    },
  };

  const editorContainerStyles: SxProps<Theme> = {
    position: 'relative',
    ...(minHeight && { minHeight }),
    ...(maxHeight && { maxHeight, overflow: 'auto' }),
    ...(error && {
      '& .MuiPaper-root': {
        borderColor: 'error.main',
      },
    }),
  };

  const previewPanelStyles: SxProps<Theme> = {
    p: 2,
    backgroundColor: theme.palette.action.hover,
    borderRadius: 1,
    ...(minHeight && { minHeight }),
    ...(maxHeight && { maxHeight, overflow: 'auto' }),
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================

  // Determine CSS class for state
  const stateClass = [
    error && 'Mui-error',
    disabled && 'Mui-disabled',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      data-testid="mention-prompt-field"
      className={stateClass}
      sx={containerStyles}
    >
      {/* Label */}
      {label && (
        <Typography variant="subtitle2" sx={labelStyles}>
          {label}
        </Typography>
      )}

      {/* Expression/Result Toggle */}
      <ToggleButtonGroup
        value={currentPreviewMode}
        exclusive
        onChange={handlePreviewModeChange}
        aria-label="Preview mode"
        size="small"
        sx={toggleGroupStyles}
        disabled={disabled}
      >
        <ToggleButton
          value="expression"
          aria-pressed={currentPreviewMode === 'expression'}
          disabled={disabled}
        >
          Expression
        </ToggleButton>
        <ToggleButton
          value="result"
          aria-pressed={currentPreviewMode === 'result'}
          disabled={disabled}
        >
          Result
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Editor (Expression Mode) */}
      {currentPreviewMode === 'expression' && (
        <Box data-testid="editor-container" sx={editorContainerStyles}>
          <RichMentionEditorWrapper
            value={internalValue}
            onChange={handleChange}
            currentProjectId={projectId}
            currentUser={currentUser}
            project={project}
            audiences={editorProps.audiences}
            actorsByAudience={editorProps.actorsByAudience}
            tasks={editorProps.tasks}
            studyCases={editorProps.studyCases}
            placeholder={placeholder}
            readOnly={readOnly || disabled}
            error={error}
          />
        </Box>
      )}

      {/* Preview Panel (Result Mode) */}
      {currentPreviewMode === 'result' && (
        <Paper
          data-testid="preview-panel"
          variant="outlined"
          sx={previewPanelStyles}
          role="region"
          aria-label="Preview content"
        >
          {previewContent || (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ fontStyle: 'italic' }}
            >
              No content to preview
            </Typography>
          )}
        </Paper>
      )}

      {/* Error Message */}
      {error && errorMessage && (
        <FormHelperText error sx={{ mt: 0.5 }}>
          {errorMessage}
        </FormHelperText>
      )}

      {/* Helper Text (hidden when error shown) */}
      {!error && helperText && (
        <FormHelperText sx={{ mt: 0.5 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default MentionPromptField;
